import { page } from '$app/stores';
import { derived, type Readable } from 'svelte/store';
import { lang } from '$lib/lang';
import { objectKeys } from '$lib/utils/object';
import data from './data.json';
import { isSupportedUSCountry, optimalUSCountry } from '$lib/country';

export const defaultRegion = 'us';
export const defaultCurrency = 'USD';

// shopify regions
export const regions = {
  eu: 'Europe',
  us: 'United States',
  uk: 'United Kingdom'
} as const;

export type Location = {
  countryCode: string;
  currency: string;
  currencySymbol: string;
  flag: string;
  languages: string[];
  mainLanguage: string;
  name: string;
  store: string;
};

export type Region = keyof typeof regions;

export const locations: Record<string, Location> = data || [];
export type CountryCode = keyof typeof locations;

export const isLocation = (region: string | number | null): boolean => {
  return region === null || Object.keys(locations).includes(region?.toString());
};

export const isRegion = (region: string | number | null): region is Region => {
  return region === null || Object.keys(regions).includes(region?.toString());
};

export const getLocationStore = (region: string | number | null): string => {
  return region && isLocation(region) ? locations[region].store : defaultRegion;
};

export const region: Readable<Region> = derived(page, ($page) => {
  const locationStore = getLocationStore($page.params.region);
  return isRegion(locationStore) ? locationStore : defaultRegion;
});

export const currentLocation: Readable<string> = derived(page, ($page) => {
  if ($page.params.region && isSupportedUSCountry($page.params.region.toUpperCase())) {
    optimalUSCountry.set($page.params.region.toUpperCase());
  }
  return isLocation($page.params.region) ? $page.params.region : defaultRegion;
});

const klarnaLocales = {
  en: {
    us: 'en-US',
    uk: 'en-GB',
    eu: 'en-NL'
  },
  fr: {
    us: 'en-US',
    uk: 'en-GB',
    eu: 'fr-FR'
  }
} as const;

export const klarnaRegion = derived([region, lang], ([$region, $lang]) => {
  return klarnaLocales[$lang][$region];
});

type GeoLocation = {
  ip: string;
  country_code: string | null;
  country_name: string | null;
  continent: string | null;
  region_code: string | null;
  region_name: string | null;
  city: string | null;
  zip_code: string | null;
  time_zone: string | null;
  latitude: number | null;
  longitude: number | null;
  metro_code: number | null;
};

export type FetchGeolocationInput = { ip?: string; fetch?: typeof fetch };

export const fetchGeolocation = async (
  config: FetchGeolocationInput = {}
): Promise<GeoLocation> => {
  const f = config.fetch || fetch;

  const res = await f(`https://geoip.significa.dev/geoip${config.ip ? `/${config.ip}` : ''}`);

  if (!res.ok) throw new Error('Failed to fetch geolocation');

  return res.json();
};

const optimalRegionToCountry = {
  uk: [
    'GB', // United Kingdom
    'JE', // Jersey
    'GG', // Guernsey
    'GI' // Gibraltar
  ],
  eu: [
    'AT', // Austria
    'BE', // Belgium
    'BG', // Bulgaria
    'HR', // Croatia
    'CY', // Cyprus
    'CZ', // Czech Republic
    'DK', // Denmark
    'EE', // Estonia
    'FI', // Finland
    'FR', // France
    'DE', // Germany
    'GR', // Greece
    'HU', // Hungary
    'IE', // Ireland
    'IT', // Italy
    'LV', // Latvia
    'LT', // Lithuania
    'LU', // Luxembourg
    'MT', // Malta
    'NL', // Netherlands
    'PL', // Poland
    'PT', // Portugal
    'RO', // Romania
    'SK', // Slovakia
    'SI', // Slovenia
    'ES', // Spain
    'SE', // Sweden
    'TR', // Turkey
    'RU', // Russia
    'AD', // Andorra
    'AL', // Albania
    'AX', // Åland Islands
    'BA', // Bosnia and Herzegovina
    'BY', // Belarus
    'CH', // Switzerland
    'FO', // Faroe Islands
    'IM', // Isle of Man
    'IS', // Iceland
    'LI', // Liechtenstein
    'MC', // Monaco
    'MD', // Moldova, Republic of
    'ME', // Montenegro
    'MK', // Macedonia, the former Yugoslav Republic of
    'NO', // Norway
    'RS', // Serbia
    'SJ', // Svalbard and Jan Mayen
    'SM', // San Marino
    'UA', // Ukraine
    'VA' // Holy See
  ]
};

export const getOptimalLocation = (currentCountryCode?: GeoLocation['country_code']): Region => {
  return (
    objectKeys(optimalRegionToCountry).find((region) => {
      return currentCountryCode && optimalRegionToCountry[region].includes(currentCountryCode);
    }) || defaultRegion
  );
};

export const inferRegion = async (options: FetchGeolocationInput) => {
  let region: Region = defaultRegion;
  try {
    const userCountry = await fetchGeolocation(options);
    if (userCountry.country_code) {
      region = getOptimalLocation(userCountry.country_code);
    }
  } catch (error) {
    console.error('Failed to retrieve region, using the defaultRegion', error);
  }
  return region;
};

export const currency = derived([region], ([$region]) => {
  return locations[$region]?.currency || defaultCurrency;
});

export const OLD_SHOPIFY_HOSTNAME_REGEX = /^((us|eu|uk)\.)?heyharpershop\.com$/;

type GetOldShopifyRegionInput = FetchGeolocationInput & {
  hostname: string;
};

export const getOldShopifyRegion = async (
  options: GetOldShopifyRegionInput
): Promise<Region | null> => {
  const oldShopifyHostnameMatch = options.hostname.match(OLD_SHOPIFY_HOSTNAME_REGEX);
  if (!oldShopifyHostnameMatch) {
    return null;
  }

  const region = oldShopifyHostnameMatch[2];

  if (isRegion(region)) {
    return region;
  }

  // The user came from the old website (or a similar pattern), but we failed to extract the region: infer it
  return await inferRegion(options);
};
